import { motion } from "framer-motion";
import { Reveal } from "../lib/motion";
import { useSeo } from "../lib/seo";
import { Eyebrow, SectionHead, PrimaryCTA, Section } from "../components/p1/Primitives";
import { ABOUT } from "../lib/phase1";

const fade = (delay) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
});

const Hero = () => (
  <section className="relative overflow-hidden border-b border-border bg-background pt-[72px] rule-grid" data-testid="about-hero">
    <div className="container-x relative py-24 md:py-32">
      <motion.div {...fade(0.05)}><Eyebrow testid="about-eyebrow">{ABOUT.hero.eyebrow}</Eyebrow></motion.div>
      <motion.h1 {...fade(0.15)} data-testid="about-title" className="mt-7 max-w-4xl text-balance text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl">
        {ABOUT.hero.h1}
      </motion.h1>
      <motion.p {...fade(0.3)} data-testid="about-copy" className="mt-8 max-w-2xl text-lg leading-relaxed text-muted-foreground">
        {ABOUT.hero.copy}
      </motion.p>
    </div>
  </section>
);

const Why = () => (
  <Section testid="about-why">
    <div className="grid gap-12 lg:grid-cols-12">
      <SectionHead title={ABOUT.why.h2} className="lg:col-span-6" testid="about-why-title" />
      <Reveal delay={0.1} className="lg:col-span-6 lg:pt-2">
        {ABOUT.why.body.map((p, i) => (
          <p key={i} className={`leading-relaxed ${i ? "mt-5 font-medium text-foreground" : "text-muted-foreground"}`}>{p}</p>
        ))}
      </Reveal>
    </div>
  </Section>
);

const Beliefs = () => (
  <Section dark testid="about-beliefs" className="rule-grid-light">
    <Eyebrow light>What we believe</Eyebrow>
    <ol className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-white/12 bg-white/12 md:grid-cols-2">
      {ABOUT.beliefs.map((b, i) => (
        <Reveal key={i} as="li" delay={i * 0.08} className="bg-[#011E6E] p-8 md:p-10">
          <span className="display text-3xl text-white/40">{i + 1}</span>
          <h3 className="mt-6 text-xl font-semibold leading-snug text-white md:text-2xl">{b.title}</h3>
          <p className="mt-4 leading-relaxed text-white/70">{b.text}</p>
        </Reveal>
      ))}
    </ol>
  </Section>
);

const FounderProfile = () => (
  <Section testid="about-founder">
    <div className="grid items-start gap-12 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <SectionHead eyebrow="Founder" title={ABOUT.founder.h2} testid="about-founder-title" />
        <Reveal delay={0.1} className="mt-8 max-w-2xl space-y-5">
          {ABOUT.founder.body.map((p, i) => <p key={i} className="leading-relaxed text-muted-foreground">{p}</p>)}
          <p className="border-l-2 border-[#0133BA] pl-4 text-sm font-medium leading-relaxed dark:border-primary" data-testid="about-founder-disclaimer">{ABOUT.founder.disclaimer}</p>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="lg:col-span-5">
        <div className="max-w-[320px] overflow-hidden rounded-2xl border border-border lg:max-w-none">
          <img src="/brand/founder.jpg" alt="Arunava Ghosh, Founder of Zenspark Global" className="aspect-[4/5] w-full object-cover object-top" />
        </div>
        <p className="mt-4 text-sm font-semibold">Arunava Ghosh</p>
        <p className="label mt-1 text-muted-foreground">Founder · Zenspark Global</p>
      </Reveal>
    </div>
  </Section>
);

const Identity = () => (
  <Section tint testid="about-identity">
    <dl className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
      {ABOUT.identity.map(([k, v], i) => (
        <Reveal key={k} delay={i * 0.06} className="bg-background p-7">
          <dt className="label text-muted-foreground">{k}</dt>
          <dd className="mt-3 font-semibold leading-snug" data-testid={`identity-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</dd>
        </Reveal>
      ))}
    </dl>
  </Section>
);

const Closing = () => (
  <Section dark testid="about-closing" className="rule-grid-light">
    <div className="flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-end">
      <SectionHead light title={ABOUT.closing.h2} testid="about-closing-title" />
      <Reveal delay={0.1}><PrimaryCTA to="/contact" light testid="about-closing-cta">{ABOUT.closing.cta}</PrimaryCTA></Reveal>
    </div>
  </Section>
);

export default function About() {
  useSeo(ABOUT.seo.title, ABOUT.seo.description);
  return (
    <div className="p1" data-testid="about-page">
      <Hero />
      <Why />
      <Beliefs />
      <FounderProfile />
      <Identity />
      <Closing />
    </div>
  );
}
